import { useEffect, useMemo, useRef, useState } from 'react';
import type { MapProps } from '../components/Map/Map.types';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { debounce } from 'lodash-es';

type MapViewPort = Readonly<{ center: google.maps.LatLngLiteral; bounds: google.maps.LatLngBounds }>;

export const useMapControls = () => {
  // Ref stores the latest viewport immediately (no re-render);
  // debounced state triggers a re-render only after pan/zoom settles.
  const mapViewPortRef = useRef<MapViewPort>();
  const [mapViewPort, setMapViewPortState] = useState<MapViewPort>();
  const isMountedRef = useRef(true);

  const debouncedFlush = useMemo(
    () =>
      debounce((vp: MapViewPort) => {
        if (isMountedRef.current) {
          setMapViewPortState(vp);
        }
      }, 150),
    [],
  );

  useEffect(
    () => () => {
      isMountedRef.current = false;
      debouncedFlush.cancel();
    },
    [debouncedFlush],
  );

  const setMapViewPort = useEventCallback((viewport: MapViewPort) => {
    mapViewPortRef.current = viewport;
    debouncedFlush(viewport);
  });

  const [reCenterMap, setReCenterMap] = useState<MapProps['reCenterMap']>({ location: false, markers: false });

  const recenterMapViaMarkers = useEventCallback(() => {
    setReCenterMap(prev => ({ ...prev, markers: true }));
  });

  const recenterMapViaLocation = useEventCallback((isDefaultZoom?: boolean) => {
    setReCenterMap(prev => ({ ...prev, location: true, isDefaultZoom }));
  });

  const resetReCenterConfig = useEventCallback(() => {
    setReCenterMap({ location: false, markers: false });
  });

  const resetLocationRecenterConfig = useEventCallback(() => {
    setReCenterMap(prev => ({ ...prev, location: false }));
  });

  return {
    mapViewPort,
    setMapViewPort,

    setReCenterMap,
    reCenterMap,
    recenterMapViaMarkers,
    recenterMapViaLocation,
    resetLocationRecenterConfig,
    resetReCenterConfig,
  };
};
